import _ from 'lodash';
import md5 from 'blueimp-md5';
import io from 'socket.io-client';
import { history, matchPath } from 'umi';
import tokenEvents, { TOKEN_EVENTS } from './tokenEvents';
import { getSessionToken } from '../utils/infrastructure/auth';
import { parseJson } from '../utils/infrastructure/utils';

class SocketService {
  constructor() {
    if (SocketService._instance) {
      return SocketService._instance;
    }

    this._initializeProperties();
    this._setupTokenEventListeners();
    this._setupLifecycleListeners();
    this._startJoiningGroupsCleanup();
    SocketService._instance = this;
  }

  static getInstance() {
    if (!SocketService._instance) {
      SocketService._instance = new SocketService();
    }
    return SocketService._instance;
  }

  get connected() {
    return this.isConnected;
  }

  get token() {
    return this._getTokenFromOptions();
  }

  _initializeProperties() {
    this.socket = null;
    this.currentGroup = null;
    this.isConnected = false;
    this.messageQueue = new Map();
    this.joiningGroups = new Map();
    this.pendingJoinCallbacks = new Map();
    this.eventListeners = new Map();
    this.initialized = false;
    this.serverUrl = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 15;
    this.isReconnecting = false;
    this.reconnectDelay = 2000;
    this.maxReconnectDelay = 30000;
    // Token sent in the last handshake. The server binds the JWT at the
    // handshake, so a live socket is only usable while this matches storage.
    this._handshakeToken = null;
    // A recycle requested while another one runs; re-checked once it ends.
    this._recycleRequested = false;
    this._lastStaleRecycleAt = 0;
    // Chat room id → consumers that need it. A consumer's leaveChat only reaches
    // the server once no other consumer holds the room.
    this.chatRoomOwners = new Map();
    this.joiningGroupsCleanupInterval = null;
    this.joiningGroupsCleanupIntervalMs = 5000;
    this.joiningGroupsInFlightTtl = 15000;
    this._heartbeatInterval = null;
    this._delayedReconnectTimer = null;
    // Survives socket instance swaps so a token cycle or a context switch still
    // counts as a reconnection; only a full teardown resets it.
    this._connectionCount = 0;
  }

  /**
   * Setup listeners for token refresh events
   */
  _setupTokenEventListeners() {
    // Listen for token refresh events
    tokenEvents.on(TOKEN_EVENTS.TOKEN_REFRESHED, this._handleTokenRefresh.bind(this));
    tokenEvents.on(TOKEN_EVENTS.SOCKET_RECONNECT_NEEDED, this._handleReconnectRequest.bind(this));
  }

  /**
   * Setup page lifecycle listeners to proactively restore socket connection
   * after the app returns to foreground or network becomes online
   */
  _setupLifecycleListeners() {
    try {
      // Debounce reconnect attempts to prevent storms from rapid visibility/online changes
      this._lifecycleReconnectTimer = null;
      const debouncedReconnect = () => {
        if (this._lifecycleReconnectTimer) clearTimeout(this._lifecycleReconnectTimer);
        this._lifecycleReconnectTimer = setTimeout(() => {
          this._lifecycleReconnectTimer = null;
          this.ensureConnected();
        }, 300);
      };

      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          try {
            if (document.visibilityState === 'visible') {
              debouncedReconnect();
            }
          } catch (e) {}
        });
      }

      if (typeof window !== 'undefined') {
        window.addEventListener('online', () => {
          try {
            debouncedReconnect();
          } catch (e) {}
        });
      }
    } catch (e) {}
  }

  _startJoiningGroupsCleanup() {
    if (this.joiningGroupsCleanupInterval) return;
    this.joiningGroupsCleanupInterval = setInterval(() => {
      try {
        this._cleanupJoiningGroups();
      } catch (e) {
        if (process.env.NODE_ENV === 'development')
          console.warn('[SocketService] joiningGroups cleanup error:', e);
      }
    }, this.joiningGroupsCleanupIntervalMs);
  }

  _cleanupJoiningGroups() {
    const now = Date.now();
    this.joiningGroups.forEach((entry, key) => {
      const rooms = entry?.rooms;
      if (rooms && Object.keys(rooms).length > 0) {
        Object.keys(rooms).forEach((kind) => {
          const room = rooms[kind];
          if (
            (room?.joining || room?.disconnecting) &&
            room?.timestamp &&
            now - room.timestamp > this.joiningGroupsInFlightTtl
          ) {
            this._releaseRoom(key, kind);
            this._flushPendingJoinCallbacks(`${kind}:${key}`, null, { message: 'join_stale' });
          }
        });
        return;
      }

      const { joining, disconnecting, timestamp } = entry || {};
      if (
        (joining || disconnecting) &&
        timestamp &&
        now - timestamp > this.joiningGroupsInFlightTtl
      ) {
        this.joiningGroups.delete(key);
        this._flushPendingJoinCallbacks(key, null, { message: 'join_stale' });
      }
    });
  }

  /**
   * Queues callbacks for a join that is already in flight for the same room.
   * They are settled by `_flushPendingJoinCallbacks` once the ack arrives.
   */
  _addPendingJoinCallback(normalizedKey, onSuccess, onError) {
    if (!normalizedKey || (!onSuccess && !onError)) return;
    const existing = this.pendingJoinCallbacks.get(normalizedKey) || [];
    existing.push({ onSuccess, onError });
    this.pendingJoinCallbacks.set(normalizedKey, existing);
  }

  _flushPendingJoinCallbacks(key, result, error) {
    const normalizedKey = this._normalizeJoinKey(key);
    if (!normalizedKey) return;
    const pending = this.pendingJoinCallbacks.get(normalizedKey);
    if (!pending || pending.length === 0) return;
    this.pendingJoinCallbacks.delete(normalizedKey);
    pending.forEach(({ onSuccess, onError }) => {
      try {
        if (error) {
          if (onError) onError(error);
        } else if (onSuccess) {
          onSuccess(result);
        }
      } catch (e) {}
    });
  }

  _normalizeJoinKey(key) {
    if (!key && key !== 0) return null;
    return key.toString().toUpperCase();
  }

  /**
   * Chat and the request room share the process-instance id. They have to be
   * tracked apart: a single slot meant a later joinChat erased the
   * process-instance join, so a reconnect never sent that joinGroup again.
   */
  _roomKind(event, data) {
    if (
      event === 'joinChat' ||
      event === 'leaveChat' ||
      data?.type === 'joinChat' ||
      data?.type === 'leaveChat'
    ) {
      return 'joinChat';
    }
    return data?.type || 'group';
  }

  _queueKey(event, data) {
    const id = this._normalizeJoinKey(data?.groupId || data?.threadId);
    if (!id) return null;
    return `${this._roomKind(event, data)}:${id}`;
  }

  _callbackKey(event, data) {
    const queueKey = this._queueKey(event, data);
    return queueKey ? this._normalizeJoinKey(queueKey) : null;
  }

  /**
   * True when that exact room is joined, a join for it is in flight, or it is
   * queued for `_restoreJoins` on the next connect.
   */
  hasRoom(id, kind) {
    const key = this._normalizeJoinKey(id);
    const entry = key ? this.joiningGroups.get(key) : null;
    if (!entry || !kind) return false;
    const room = entry.rooms?.[kind];
    if (room) return !room.disconnecting;
    if (entry.rooms) return false;
    return this._roomKind(entry.event, entry.data) === kind && !entry.disconnecting;
  }

  _syncEntryFromRooms(existing, rooms) {
    const primary =
      rooms['process-instance'] ||
      rooms.all ||
      Object.values(rooms).find((room) => room?.data?.type !== 'joinChat') ||
      rooms.joinChat ||
      null;
    return {
      ...existing,
      rooms,
      data: primary?.data,
      event: primary?.event,
      joining: Object.values(rooms).some((room) => room.joining),
      disconnecting:
        Object.keys(rooms).length > 0 && Object.values(rooms).every((room) => room.disconnecting),
      timestamp: Date.now(),
    };
  }

  _markJoiningGroupEntry(key, entry) {
    const normalizedKey = this._normalizeJoinKey(key);
    if (!normalizedKey) return;
    const existing = this.joiningGroups.get(normalizedKey) || { rooms: {} };
    const kind = entry?.kind || this._roomKind(entry?.event, entry?.data);
    const rooms = { ...(existing.rooms || {}) };
    const prevRoom = rooms[kind] || {};
    rooms[kind] = {
      ...prevRoom,
      data: entry?.data || prevRoom.data,
      event: entry?.event || prevRoom.event || (kind === 'joinChat' ? 'joinChat' : 'joinGroup'),
      joining: entry?.joining ?? prevRoom.joining ?? false,
      disconnecting: entry?.disconnecting ?? false,
      pendingRestore: entry?.pendingRestore ?? false,
      timestamp: Date.now(),
    };
    this.joiningGroups.set(normalizedKey, this._syncEntryFromRooms(existing, rooms));
  }

  _releaseRoom(key, kind) {
    const normalizedKey = this._normalizeJoinKey(key);
    if (!normalizedKey) return;
    const existing = this.joiningGroups.get(normalizedKey);
    if (!existing) return;
    if (!kind || !existing.rooms) {
      this.joiningGroups.delete(normalizedKey);
      return;
    }
    const rooms = { ...existing.rooms };
    delete rooms[kind];
    if (Object.keys(rooms).length === 0) {
      this.joiningGroups.delete(normalizedKey);
      return;
    }
    this.joiningGroups.set(normalizedKey, this._syncEntryFromRooms(existing, rooms));
  }

  _releaseJoiningGroupEntry(key) {
    const normalizedKey = this._normalizeJoinKey(key);
    if (!normalizedKey) return;
    this.joiningGroups.delete(normalizedKey);
  }

  /**
   * 0 connecting, 1 open. Calling connect() in either state opens a second
   * websocket while the first is still listed as pending.
   */
  _transportReadyState() {
    try {
      const transport = this.socket?.io?.engine?.transport;
      const ws = transport?.ws || transport?.socket;
      if (ws && typeof ws.readyState === 'number') return ws.readyState;
      const ready = this.socket?.io?.engine?.readyState;
      if (ready === 'open') return 1;
      if (ready === 'opening') return 0;
    } catch (e) {}
    return null;
  }

  _transportIsLive() {
    const state = this._transportReadyState();
    return state === 0 || state === 1;
  }

  _isJoinEvent(event, data) {
    return (
      event === 'joinChat' ||
      event === 'joinGroup' ||
      data?.type === 'joinChat' ||
      data?.type === 'joinGroup'
    );
  }

  _isLeaveEvent(event, data) {
    return event === 'leaveGroup' || data?.type === 'leaveChat' || data?.type === 'leaveGroup';
  }

  /**
   * socket.io calls a function `auth` on every handshake, automatic
   * reconnections included, so each one carries the token currently in
   * storage. A static `{ token }` would replay the token captured when the
   * socket was created. An explicit `initialToken` is honored for the first
   * handshake and remains the fallback when storage has none.
   */
  _buildAuth(initialToken = null) {
    let pendingInitial = initialToken;
    return (cb) => {
      const token = pendingInitial || this._getTokenFromOptions() || initialToken;
      pendingInitial = null;
      this._handshakeToken = token;
      cb({ token });
    };
  }

  /** True when the live session was opened with the token in storage. */
  _handshakeIsCurrent() {
    const stored = this._getTokenFromOptions();
    return !stored || stored === this._handshakeToken;
  }

  /**
   * Recycles a connected socket whose handshake token is no longer the stored
   * one, e.g. another tab refreshed it. useTokenRefresh skips the refresh in
   * that case because the stored token is fresh, so nobody else would.
   * Throttled so a burst of 401s cannot loop.
   */
  _recycleIfHandshakeStale() {
    if (!this.serverUrl || !this.socket || !this.socket.connected) return false;
    if (this._handshakeIsCurrent()) return false;
    const now = Date.now();
    if (now - this._lastStaleRecycleAt < 10000) return false;
    this._lastStaleRecycleAt = now;
    this._attemptReconnectWithToken(this._getTokenFromOptions()).catch(() => {});
    return true;
  }

  /** Exponential backoff capped at `maxReconnectDelay`, with ±20% jitter. */
  _reconnectBackoffMs(attempt) {
    const base = Math.min(
      this.maxReconnectDelay,
      this.reconnectDelay * Math.pow(2, Math.max(0, attempt - 1)),
    );
    return Math.min(this.maxReconnectDelay, Math.round(base * (0.8 + Math.random() * 0.4)));
  }

  /**
   * Drops a socket instance for good: external listeners move to the next
   * instance through `_rebindEventListeners`, so leaving them here would leak
   * the old socket and its closures on every token cycle.
   */
  _discardSocket() {
    const oldSocket = this.socket;
    if (!oldSocket) return;
    this._offAll(true);
    if (typeof oldSocket.offAny === 'function') oldSocket.offAny();
    // Disconnecting first still runs the internal 'disconnect' handler, which
    // marks rooms for restore and notifies consumers.
    oldSocket.disconnect();
    if (typeof oldSocket.off === 'function') oldSocket.off();
  }

  /**
   * Periodic heartbeat to detect and recover dead connections.
   * If socket is disconnected and not already reconnecting, triggers reconnection.
   */
  _startHeartbeat() {
    this._stopHeartbeat();
    this._heartbeatInterval = setInterval(() => {
      try {
        if (this.socket && this.socket.connected) return;
        if (!this.serverUrl || this.isReconnecting) return;
        // A background tab can miss a ping and look disconnected while the
        // websocket is still open. connect() would open a second one.
        if (this._transportIsLive()) return;
        const token = this._getTokenFromOptions();
        if (token) {
          // If the socket exists but is not connected, try calling connect()
          // which resumes socket.io's built-in reconnection; the auth callback
          // reads the current token.
          if (this.socket && !this.socket.connected) {
            try {
              this.socket.connect();
            } catch (e) {}
          } else if (!this.socket) {
            this._attemptReconnectWithToken(token).catch(() => {});
          }
        }
      } catch (e) {}
    }, 30000);
  }

  _stopHeartbeat() {
    if (this._heartbeatInterval) {
      clearInterval(this._heartbeatInterval);
      this._heartbeatInterval = null;
    }
  }

  /**
   * Public API: ensures the socket is connected.
   * If disconnected, updates auth token and kicks off reconnection.
   * Returns true if currently connected.
   */
  ensureConnected() {
    if (this.socket && this.socket.connected) return true;
    if (!this.serverUrl) return false;
    if (this._transportIsLive()) return true;

    const token = this._getTokenFromOptions();

    if (this.socket) {
      // Resume socket.io built-in reconnection
      if (!this.socket.connected) {
        try {
          this.socket.connect();
        } catch (e) {}
      }
    } else if (token && !this.isReconnecting) {
      // No socket instance at all — create fresh
      this._attemptReconnectWithToken(token).catch(() => {});
    }

    return this.isConnected;
  }

  /**
   * Resolves true once the connection is live, false on timeout.
   *
   * Event-driven rather than a short polling window: rebuilding the connection
   * after a token refresh or a company/environment switch can easily take
   * longer than a few seconds, and the socket instance itself is replaced while
   * we wait. Listening for the service-level `socketServiceConnected` signal
   * keeps this correct across those swaps, and `ensureConnected` is re-kicked so
   * a dead socket does not sit idle until the next heartbeat.
   */
  waitForConnection({ timeoutMs = 20000 } = {}) {
    if (this.socket && this.socket.connected) return Promise.resolve(true);

    return new Promise((resolve) => {
      let settled = false;

      const finish = (ok) => {
        if (settled) return;
        settled = true;
        if (typeof window !== 'undefined') {
          window.removeEventListener('socketServiceConnected', onConnected);
        }
        clearInterval(watchdog);
        clearTimeout(timer);
        resolve(ok);
      };

      const onConnected = () => finish(true);

      if (typeof window !== 'undefined') {
        window.addEventListener('socketServiceConnected', onConnected);
      }

      // Covers a connect that lands before the listener attaches, and keeps
      // nudging the transport for the whole wait.
      let ticks = 0;
      const watchdog = setInterval(() => {
        if (this.socket && this.socket.connected) {
          finish(true);
          return;
        }
        ticks += 1;
        if (ticks % 4 === 0) this.ensureConnected();
      }, 500);

      const timer = setTimeout(() => finish(false), timeoutMs);

      this.ensureConnected();
    });
  }

  /**
   * Handle token refresh event.
   * A disconnected socket resumes on its own with the fresh token (the auth
   * callback reads storage). Avoids destroying the socket unnecessarily.
   */
  async _handleTokenRefresh(data) {
    const { token } = data;
    if (!this.serverUrl) return;

    if (this.socket && !this.socket.connected) {
      // Socket exists but is disconnected — resume connection with updated auth.
      try {
        this.socket.connect();
      } catch (e) {}
    } else if (!this.socket && token) {
      // Socket was destroyed — recreate
      await this._attemptReconnectWithToken(token);
    }
    // If already connected, no action needed; connection continues with current session.
  }

  /**
   * Handle reconnection request (e.g. after a token refresh).
   *
   * The JWT used by the server is the one captured at the WebSocket
   * *handshake*, so a connected socket must be cycled whenever its handshake
   * token is no longer the stored one, otherwise emits keep being rejected
   * with 401 until the connection is re-established. Deciding on the token
   * rather than on `reason` covers every refresh path and skips a needless
   * cycle when the socket already reconnected with the new token.
   */
  async _handleReconnectRequest(data) {
    if (!this.serverUrl) return;

    const token = this._getTokenFromOptions();
    const requiresHandshakeRefresh = data?.reason === 'force' || !this._handshakeIsCurrent();

    if (this.socket) {
      if (this.socket.connected) {
        if (requiresHandshakeRefresh && token) {
          await this._attemptReconnectWithToken(token);
        }
        return;
      }

      try {
        this.socket.connect();
      } catch (e) {}
    } else if (token) {
      await this._attemptReconnectWithToken(token);
    }
  }

  /**
   * Attempt to reconnect with new token
   * @param {string} newToken - New access token
   */
  async _attemptReconnectWithToken(newToken) {
    if (this.isReconnecting) {
      // The running cycle may already be past its handshake with the old token.
      this._recycleRequested = true;
      return;
    }

    this.isReconnecting = true;
    this._recycleRequested = false;
    this.reconnectAttempts = 0;

    try {
      await this._performReconnection(newToken);

      // Emit success event
      tokenEvents.emit(TOKEN_EVENTS.SOCKET_RECONNECTED, {
        timestamp: Date.now(),
        attempts: this.reconnectAttempts + 1,
      });
    } catch (error) {
      // Emit failure event
      tokenEvents.emit(TOKEN_EVENTS.SOCKET_RECONNECT_FAILED, {
        error,
        attempts: this.reconnectAttempts + 1,
        timestamp: Date.now(),
      });

      // Don't give up forever — schedule a delayed retry so the heartbeat or
      // a later ensureConnected() call can pick it up.
      this._scheduleDelayedReconnect();
    } finally {
      this.isReconnecting = false;
      if (this._recycleRequested) {
        this._recycleRequested = false;
        if (this.serverUrl && this.socket?.connected && !this._handshakeIsCurrent()) {
          this._attemptReconnectWithToken(this._getTokenFromOptions()).catch(() => {});
        }
      }
    }
  }

  /**
   * Schedules a one-shot reconnection attempt after a cooldown period.
   * Prevents permanent "dead socket" state after max retries are exhausted.
   */
  _scheduleDelayedReconnect() {
    if (this._delayedReconnectTimer) return;
    this._delayedReconnectTimer = setTimeout(() => {
      this._delayedReconnectTimer = null;
      if (this.isConnected || !this.serverUrl) return;
      const token = this._getTokenFromOptions();
      if (token && !this.isReconnecting) {
        this._attemptReconnectWithToken(token).catch(() => {});
      }
    }, 30000);
  }

  /**
   * Perform the actual reconnection with retry logic
   * @param {string} newToken - New access token
   */
  async _performReconnection(newToken) {
    while (this.reconnectAttempts < this.maxReconnectAttempts) {
      try {
        if (this.reconnectAttempts > 0) {
          // Capped: the heartbeat stands down while a cycle runs, so an
          // uncapped wait would leave a dead link alone for hours.
          await new Promise((resolve) =>
            setTimeout(resolve, this._reconnectBackoffMs(this.reconnectAttempts)),
          );
          // The previous attempt's socket keeps retrying on its own, and
          // visibility/online handlers can revive it too. Its handshake reads
          // the current token, so a live one is kept rather than torn down.
          if (!this.serverUrl) {
            const aborted = new Error('Socket service torn down');
            aborted.aborted = true;
            throw aborted;
          }
          if (this.socket && this.socket.connected && this._handshakeIsCurrent()) {
            this.reconnectAttempts = 0;
            this._startHeartbeat();
            return;
          }
        }

        this._discardSocket();

        this.socket = io(this.serverUrl, {
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          auth: this._buildAuth(newToken),
          transports: ['websocket'],
          path: '/ws',
        });

        // Setup events for new socket
        // Reset initialized flag to configure listeners for the new socket instance
        this.initialized = false;
        this._setupSocketEvents();
        this._startJoiningGroupsCleanup();
        // Rebind any external listeners registered via SocketService.on(...)
        this._rebindEventListeners();

        // Wait for connection
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Connection timeout'));
          }, 10000);

          this.socket.once('connect', () => {
            clearTimeout(timeout);
            resolve();
          });

          this.socket.once('connect_error', (error) => {
            clearTimeout(timeout);
            reject(error);
          });
        });

        // Success - break out of retry loop
        this.reconnectAttempts = 0;
        this._startHeartbeat();
        return;
      } catch (error) {
        if (error?.aborted) throw error;
        this.reconnectAttempts++;

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          throw new Error(
            `Failed to reconnect after ${this.maxReconnectAttempts} attempts: ${error.message}`,
          );
        }
      }
    }
  }

  _getTokenFromOptions() {
    return this._readStoredToken()?.accessToken || null;
  }

  /**
   * Returns the full stored token object (accessToken, expiresIn, issuedAt, ...)
   * for the current session or external URL context. Used by callers that need
   * expiration metadata, not just the bearer string.
   */
  _readStoredToken() {
    let tokenStoredKey = 'token';

    const pathname = _.get(history.location, 'pathname');
    const externalUrlMatch = matchPath(pathname, { path: '/external/', exact: false });

    if (externalUrlMatch) {
      const { publicCode, code } = history.location.query || {};
      tokenStoredKey = publicCode ? `publicToken"${publicCode}"` : `externalToken"${md5(code)}"`;
    }

    try {
      return externalUrlMatch
        ? parseJson(localStorage.getItem(`x-auth-${tokenStoredKey}`))
        : getSessionToken();
    } catch (e) {
      return null;
    }
  }

  /**
   * Returns the time (in ms) until the stored token expires. Negative means
   * already expired. `Infinity` means no expiration metadata available
   * (treat as valid to avoid false positives).
   */
  _getTokenTimeToExpiryMs() {
    const stored = this._readStoredToken();
    if (!stored || !stored.expiresIn) return Infinity;
    const issuedAt = stored.issuedAt || Date.now();
    const expiresAt = issuedAt + stored.expiresIn * 1000;
    return expiresAt - Date.now();
  }

  /**
   * Returns true when the stored token is already expired or will expire
   * within `bufferMs` (default 30s). Use to decide whether a request that
   * goes through the socket must wait for a refresh before being emitted.
   */
  isTokenExpiredOrExpiring(bufferMs = 30000) {
    const ttl = this._getTokenTimeToExpiryMs();
    if (ttl === Infinity) return false; // unknown → don't block
    return ttl <= bufferMs;
  }

  /**
   * Ensures the auth token is fresh AND the socket session uses it before
   * performing a sensitive operation (e.g. sending a chat message).
   *
   * The server-side JWT is tied to the WebSocket handshake, so a fresh token
   * in storage is not enough: the connected socket must have been opened
   * with it. Right after a refresh the old socket is still connected until
   * `_handleReconnectRequest` cycles it, and emitting in that window gets a
   * 401 or loses the ack to the disconnect.
   *
   * Returns true when the token is fresh AND the connected socket's handshake
   * used it. Returns false on timeout or refresh failure — the caller may
   * still try the operation; failures will be caught by the 401 detection in
   * `_emitMessage`, which schedules a retry.
   */
  async ensureValidToken({ bufferMs = 30000, timeoutMs = 10000 } = {}) {
    const tokenIsFresh = () => !this.isTokenExpiredOrExpiring(bufferMs);
    const sessionIsCurrent = () =>
      Boolean(this.socket && this.socket.connected) && this._handshakeIsCurrent();
    const isReady = () => tokenIsFresh() && sessionIsCurrent();

    if (isReady()) return true;

    return new Promise((resolve) => {
      let settled = false;
      const hasWindow = typeof window !== 'undefined';

      const cleanup = () => {
        unsubscribeRefreshed();
        unsubscribeFailed();
        unsubscribeReconnected();
        unsubscribeReconnectFailed();
        if (hasWindow) window.removeEventListener('socketServiceConnected', maybeResolve);
        clearTimeout(timer);
      };

      const finish = (ok) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(ok);
      };

      const maybeResolve = () => {
        if (isReady()) finish(true);
      };

      const unsubscribeRefreshed = tokenEvents.on(TOKEN_EVENTS.TOKEN_REFRESHED, maybeResolve);
      const unsubscribeFailed = tokenEvents.on(TOKEN_EVENTS.TOKEN_REFRESH_FAILED, () =>
        finish(false),
      );
      const unsubscribeReconnected = tokenEvents.on(TOKEN_EVENTS.SOCKET_RECONNECTED, maybeResolve);
      const unsubscribeReconnectFailed = tokenEvents.on(TOKEN_EVENTS.SOCKET_RECONNECT_FAILED, () =>
        finish(false),
      );
      // socket.io's own reconnections never raise SOCKET_RECONNECTED.
      if (hasWindow) window.addEventListener('socketServiceConnected', maybeResolve);

      const timer = setTimeout(() => finish(false), timeoutMs);

      if (tokenIsFresh()) {
        // Fresh token on a stale handshake (another tab refreshed it, or the
        // cycle after this tab's refresh has not run yet) → cycle the socket.
        // A disconnected socket just needs a nudge: its handshake reads storage.
        if (!this._recycleIfHandshakeStale()) this.ensureConnected();
        return;
      }

      // Otherwise kick off the refresh via the SOCKET_AUTH_ERROR channel,
      // which `useTokenRefresh` listens to. Reuses the proven flow used
      // when the server returns 401 during an emit.
      tokenEvents.emit(TOKEN_EVENTS.SOCKET_AUTH_ERROR, {
        error: { message: 'token_expiring', reason: 'pre_emit_check' },
        timestamp: Date.now(),
        socketService: this,
      });
    });
  }

  /**
   *
   * @param {String} serverUrl
   * @param {[String]} uToken
   */
  init(serverUrl, uToken = null) {
    // Si ya hay un socket conectado, o el websocket sigue abierto, no crear otro.
    // Un segundo io() es lo que deja dos conexiones al volver a la pestaña.
    if (
      this.socket &&
      (this.socket.connected || this._transportIsLive()) &&
      this.socket.io?.uri === serverUrl
    ) {
      this.serverUrl = serverUrl;
      return;
    }

    // If there's a socket but not connected or different URL, disconnect first.
    // Listeners are preserved so `_rebindEventListeners` below can re-attach
    // them to the new socket instance.
    if (this.socket) {
      this.disconnect({ preserveListeners: true });
    }

    // Must be assigned after disconnect(), which resets it to null. Every
    // recovery path (heartbeat, ensureConnected, token refresh) bails out on a
    // null serverUrl, so losing it here disables reconnection entirely.
    this.serverUrl = serverUrl;

    this.socket = io(serverUrl, {
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      auth: this._buildAuth(uToken),
      transports: ['websocket'],
      path: '/ws',
    });

    this._setupSocketEvents();
    this._startJoiningGroupsCleanup();
    this._rebindEventListeners();
    this._startHeartbeat();
  }

  _setupSocketEvents() {
    // Verify socket exists and is not already configured
    if (!this.socket || this.initialized) return;

    this.initialized = true;

    // socket.io v4 emits `reconnect` on the Manager only, never on the Socket,
    // so every reconnection (automatic or a rebuilt instance) arrives here.
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      this._connectionCount += 1;
      const isReconnect = this._connectionCount > 1;
      this._processQueue();
      // Restore previously joined groups/rooms immediately
      this._restoreJoins();
      // Notify consumers so they can re-register listeners after a full
      // teardown, where `disconnect()` clears the registry on purpose.
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('socketServiceConnected'));
          // The backend does not replay events emitted during the gap, so
          // consumers must refetch whatever they display.
          if (isReconnect) {
            window.dispatchEvent(new CustomEvent('socketServiceReconnected'));
          }
        }
      } catch (e) {}
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      try {
        if (typeof window !== 'undefined') window.__supportChatCanSend = false;
      } catch (e) {}
      // Keep the rooms so _restoreJoins re-emits them on connect, but stop
      // reporting them as joined: the server dropped them with the link.
      this._markRoomsForRestore();
      // Fires for socket recycles too, where `_offAll` already detached the
      // consumers' own 'disconnect' listeners.
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('socketServiceDisconnected', { detail: { reason } }),
          );
        }
      } catch (e) {}
    });

    this.socket.on('connect_error', (err) => {
      this.isConnected = false;

      // Check if it's an authentication error (401). The hub rejects an expired
      // handshake token with INVALID_TOKEN and socket.io does not retry it, so
      // nothing reconnects until a refresh puts a new token in storage.
      if (
        err?.message === 'Authentication error' ||
        err?.data?.statusCode === 401 ||
        err?.data?.code === 'INVALID_TOKEN' ||
        err?.type === 'AuthenticationError' ||
        (err?.message && (err.message.includes('401') || /invalid_token/i.test(err.message)))
      ) {
        // Emit token event for authentication errors
        tokenEvents.emit(TOKEN_EVENTS.SOCKET_AUTH_ERROR, {
          error: err,
          timestamp: Date.now(),
          socketService: this,
        });

        // Also dispatch custom event for backward compatibility
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('socketAuthError', {
              detail: { error: err, socketService: this },
            }),
          );
        }
      }
      // If network is back later or visibility changes, lifecycle listeners will trigger reconnection
    });

    // Global listener to detect invalid_token messages from any channel
    // and trigger the authentication error flow
    if (typeof this.socket.onAny === 'function') {
      this.socket.onAny((event, data) => {
        try {
          if (data && typeof data === 'object' && data.message === 'invalid_token') {
            tokenEvents.emit(TOKEN_EVENTS.SOCKET_AUTH_ERROR, {
              error: { message: 'invalid_token', event },
              timestamp: Date.now(),
              socketService: this,
            });
            this._recycleIfHandshakeStale();

            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('socketAuthError', {
                  detail: { error: { message: 'invalid_token', event }, socketService: this },
                }),
              );
            }
          }
        } catch (e) {
          // Silent fail
        }
      });
    }
  }

  /**
   * Rebind previously registered external event listeners to the current socket instance
   */
  _rebindEventListeners() {
    if (!this.socket || !this.eventListeners || this.eventListeners.size === 0) return;
    this.eventListeners.forEach((callbacks, event) => {
      if (callbacks && callbacks.forEach) {
        callbacks.forEach((cb) => {
          try {
            this.socket.on(event, cb);
          } catch (e) {}
        });
      }
    });
  }

  /**
   * Called when the link drops. Rooms stay tracked for `_restoreJoins` but
   * become `pendingRestore`, so `send()` makes callers wait for the restore
   * ack instead of answering with the pre-disconnect success. A leave in
   * flight is complete: the server dropped the room with the connection.
   */
  _markRoomsForRestore() {
    Array.from(this.joiningGroups.entries()).forEach(([key, entry]) => {
      if (!entry?.rooms) return;
      const rooms = {};
      Object.entries(entry.rooms).forEach(([kind, room]) => {
        if (!room) return;
        if (room.disconnecting) {
          if (
            kind !== 'joinChat' &&
            this._normalizeJoinKey(this.currentGroup?.groupId) === key &&
            this.currentGroup?.type === kind
          ) {
            this.currentGroup = null;
          }
          return;
        }
        rooms[kind] = { ...room, joining: false, pendingRestore: true, timestamp: Date.now() };
      });
      if (Object.keys(rooms).length === 0) {
        this.joiningGroups.delete(key);
      } else {
        this.joiningGroups.set(key, this._syncEntryFromRooms(entry, rooms));
      }
    });
  }

  /**
   * Re-emit join messages for previously joined groups/rooms after reconnection
   */
  _restoreJoins() {
    if (!this.socket) return;
    try {
      Array.from(this.joiningGroups.entries()).forEach(([key, entry]) => {
        if (!entry?.rooms || Object.keys(entry.rooms).length === 0) {
          if (entry?.data && !entry.disconnecting) {
            const legacyEvent =
              entry.event === 'joinChat' || entry.data.type === 'joinChat'
                ? 'joinChat'
                : 'joinGroup';
            this._emitMessage(legacyEvent, entry.data, null, null);
          }
          return;
        }
        Object.entries(entry.rooms).forEach(([kind, room]) => {
          if (!room?.data || room.disconnecting) return;
          const eventName =
            room.event === 'joinChat' || room.data.type === 'joinChat' ? 'joinChat' : 'joinGroup';
          // In flight until the ack: `_handleMessageAck` confirms it and
          // settles the callers queued meanwhile; a failure releases it.
          this._markJoiningGroupEntry(key, {
            data: room.data,
            event: room.event,
            kind,
            joining: true,
          });
          this._emitMessage(eventName, room.data, null, null);
        });
      });
    } catch (e) {}
  }

  send(event, data, onSuccess, onError) {
    if (!data.groupId && !data.threadId) return;

    const key = data.groupId || data.threadId;
    const normalizedKey = this._normalizeJoinKey(key);
    const isJoinEvent = this._isJoinEvent(event, data);
    const kind = this._roomKind(event, data);
    const joinEntry = normalizedKey ? this.joiningGroups.get(normalizedKey) : null;
    const room = joinEntry?.rooms?.[kind];
    const sameRoom =
      room ||
      (!joinEntry?.rooms && joinEntry && this._roomKind(joinEntry.event, joinEntry.data) === kind
        ? joinEntry
        : null);

    // Dedup only the same room. A joinChat for this id must not swallow the
    // process-instance joinGroup, and the other way around.
    // An entry mid-leave falls through and is emitted again, and so does one
    // still awaiting restore on a live link (the restore never went out).
    if (
      isJoinEvent &&
      sameRoom &&
      !sameRoom.disconnecting &&
      !(sameRoom.pendingRestore && this.isConnected)
    ) {
      // A room awaiting restore was dropped by the server with the old link:
      // answer once the restore is acked, not with the stale success.
      if (sameRoom.joining || sameRoom.pendingRestore) {
        this._addPendingJoinCallback(this._callbackKey(event, data), onSuccess, onError);
      } else if (onSuccess) {
        onSuccess(sameRoom.data || data);
      }
      return;
    }

    if (this.isConnected) {
      if (isJoinEvent) {
        this._markJoiningGroupEntry(normalizedKey || key, {
          data,
          event,
          kind,
          joining: true,
          disconnecting: false,
        });
      }
      this._emitMessage(event, data, onSuccess, onError);
    } else {
      const queueKey = this._queueKey(event, data) || String(key);
      if (!this.messageQueue.has(queueKey)) {
        this.messageQueue.set(queueKey, { event, data, onSuccess, onError });
      }
    }
  }

  leaveGroup(onSuccess = null, onError = null) {
    if (!this.currentGroup) return;

    // Safety: never leave a chat room via leaveGroup — use leaveChat instead
    if (this.currentGroup.type === 'joinChat' || this.currentGroup.threadId) {
      if (onSuccess) onSuccess();
      return;
    }

    const key = this.currentGroup.groupId;
    const kind = this.currentGroup.type || 'process-instance';
    const normalizedKey = this._normalizeJoinKey(key);
    const room = this.joiningGroups.get(normalizedKey)?.rooms?.[kind];
    if (room?.disconnecting) return;

    if (!this.isConnected) {
      this._releaseRoom(key, kind);
      this.currentGroup = null;
      if (onSuccess) onSuccess();
      return;
    }

    this._markJoiningGroupEntry(key, {
      data: this.currentGroup,
      event: 'joinGroup',
      kind,
      disconnecting: true,
      joining: false,
    });
    this._leaveCurrentGroup(this.currentGroup, onSuccess, onError);
  }

  /**
   * Records that `owner` (e.g. 'support-chat', a BuilderChat instance) needs
   * the chat room, so another consumer's `leaveChat` keeps the socket in it.
   * SupportChat and BuilderChat share the DVA chat thread: without this,
   * switching builder tabs would make SupportChat leave a room BuilderChat
   * still listens to, and its replies would never arrive.
   */
  claimChatRoom(threadId, owner) {
    const key = this._normalizeJoinKey(threadId);
    if (!key || !owner) return;
    const owners = this.chatRoomOwners.get(key) || new Set();
    owners.add(owner);
    this.chatRoomOwners.set(key, owners);
  }

  /** Drops `owner`'s claim. Returns true when another consumer still holds the room. */
  releaseChatRoom(threadId, owner) {
    const key = this._normalizeJoinKey(threadId);
    const owners = key ? this.chatRoomOwners.get(key) : null;
    if (!owners) return false;
    owners.delete(owner);
    if (owners.size === 0) {
      this.chatRoomOwners.delete(key);
      return false;
    }
    return true;
  }

  isChatRoomClaimedBy(threadId, owner) {
    const key = this._normalizeJoinKey(threadId);
    return Boolean(key && this.chatRoomOwners.get(key)?.has(owner));
  }

  /** True when some consumer other than `owner` holds the chat room. */
  isChatRoomHeldByOthers(threadId, owner) {
    const key = this._normalizeJoinKey(threadId);
    const owners = key ? this.chatRoomOwners.get(key) : null;
    if (!owners) return false;
    return Array.from(owners).some((candidate) => candidate !== owner);
  }

  /**
   * @param {Object} [options]
   * @param {string} [options.owner] Releases that consumer's claim first; the
   *   leave only reaches the server once no other consumer holds the room.
   */
  leaveChat(threadId, onSuccess = null, onError = null, { owner } = {}) {
    if (!threadId) return;

    const key = threadId.toString().toUpperCase();
    if (owner && this.releaseChatRoom(key, owner)) {
      if (onSuccess) onSuccess();
      return;
    }
    this.chatRoomOwners.delete(key);
    if (this.joiningGroups.get(key)?.rooms?.joinChat?.disconnecting) return;

    if (!this.isConnected) {
      this._releaseRoom(key, 'joinChat');
      if (onSuccess) onSuccess();
      return;
    }

    const chatData = {
      type: 'leaveChat',
      threadId: key,
      groupId: key,
    };

    this._markJoiningGroupEntry(key, {
      data: chatData,
      event: 'leaveChat',
      kind: 'joinChat',
      disconnecting: true,
      joining: false,
    });
    this._leaveCurrentGroup(chatData, onSuccess, onError);
  }

  /**
   * Leaves a specific group room without touching `currentGroup`.
   * Rooms owned by chat or by the current page are kept.
   */
  leaveGroupById(groupId) {
    const key = this._normalizeJoinKey(groupId);
    if (!key) return;
    const entry = this.joiningGroups.get(key);
    const room = entry?.rooms?.['process-instance'];
    if (room?.disconnecting) return;
    if (
      this._normalizeJoinKey(this.currentGroup?.groupId) === key &&
      this.currentGroup?.type === 'process-instance'
    ) {
      return;
    }

    const data = room?.data || (entry?.data?.type === 'process-instance' ? entry.data : null);
    if (!data) return;

    if (!this.isConnected || !this.socket) {
      this._releaseRoom(key, 'process-instance');
      return;
    }

    this._markJoiningGroupEntry(key, {
      data,
      event: 'joinGroup',
      kind: 'process-instance',
      disconnecting: true,
      joining: false,
    });
    this.socket.emit('leaveGroup', data, (ok) => {
      if (ok) this._releaseRoom(key, 'process-instance');
      else this._handleMessageError('leaveGroup', data);
    });
  }

  /** True while the process-instance room is joined or a join for it is in flight. */
  isGroupJoined(groupId) {
    return this.hasRoom(groupId, 'process-instance');
  }

  hasListener(event, callback) {
    return !!this.eventListeners.get(event)?.has(callback);
  }

  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event).add(callback);
  }

  off(event, callback) {
    if (callback) {
      if (this.socket) {
        this.socket.off(event, callback);
      }
      const listeners = this.eventListeners.get(event);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.eventListeners.delete(event);
        }
      }
    } else {
      const listeners = this.eventListeners.get(event);
      if (listeners && this.socket) {
        listeners.forEach((cb) => this.socket.off(event, cb));
      }
      this.eventListeners.delete(event);
    }
  }

  /**
   * @param {Object}  [options]
   * @param {number}  [options.ackTimeoutMs] Report a missing ack through
   *   `onError` with `isTransportError: true`. Without it socket.io drops the
   *   callback silently when the connection dies before the ack, so a caller
   *   awaiting it would wait forever. A transport error means the server may or
   *   may not have received the event.
   */
  _emitMessage(event, data, onSuccess, onError, { ackTimeoutMs } = {}) {
    // If socket not connected
    if (!this.socket || !this.socket.connected) {
      // Chat messages are never queued here: SupportChat owns its own outbox so
      // it can keep the optimistic row on screen, retry on reconnect and give
      // up with a real error. Report the failure and let it decide.
      if (event === 'chatMessage') {
        if (onError) onError({ message: 'offline' });
        return;
      }
      // Joins for different rooms of the same id must not overwrite each other.
      const key = this._queueKey(event, data) || data.groupId || data.threadId;
      if (key && !this.messageQueue.has(key)) {
        this.messageQueue.set(key, { event, data, onSuccess, onError });
      }
      return;
    }

    const handleAck = (ack) => {
      // The backend may ack with one of:
      //   - boolean true  → success
      //   - boolean false → generic failure
      //   - object { ok: true,  ... } → success
      //   - object { ok: false, error_code, description } → typed failure (e.g. 401)
      // `if (ack)` alone is unsafe because `{ ok: false }` is truthy.
      const ackInfo = this._parseAck(ack);

      if (ackInfo.ok) {
        this._handleMessageAck(event, data, onSuccess);
        return;
      }

      this._handleMessageError(event, data);

      if (ackInfo.isAuthError) {
        // Surface as an auth error so the token-refresh hook kicks in and
        // any caller-side retry logic can react accordingly.
        tokenEvents.emit(TOKEN_EVENTS.SOCKET_AUTH_ERROR, {
          error: {
            message: 'unauthorized_emit',
            event,
            statusCode: ackInfo.errorCode,
            description: ackInfo.description,
          },
          timestamp: Date.now(),
          socketService: this,
        });
        this._recycleIfHandshakeStale();
      }

      if (onError) {
        onError({
          ...data,
          ackError: ackInfo,
          isAuthError: ackInfo.isAuthError,
        });
      }
    };

    if (ackTimeoutMs > 0 && typeof this.socket.timeout === 'function') {
      this.socket.timeout(ackTimeoutMs).emit(event, data, (err, ack) => {
        if (err) {
          this._handleMessageError(event, data);
          if (onError) onError({ ...data, message: 'ack_unconfirmed', isTransportError: true });
          return;
        }
        handleAck(ack);
      });
      return;
    }

    this.socket.emit(event, data, handleAck);
  }

  /**
   * Normalizes the ack payload returned by the backend into a stable shape.
   * Detects authentication failures (401 / "Unauthorized") so callers can
   * trigger a token refresh + retry instead of dropping the user message.
   */
  _parseAck(ack) {
    if (ack === true) return { ok: true, raw: ack };
    if (ack === false) {
      return { ok: false, isAuthError: false, errorCode: null, description: null, raw: ack };
    }

    if (ack && typeof ack === 'object') {
      const ok = ack.ok === true;
      const errorCode = ack.error_code ?? ack.errorCode ?? null;
      const description = ack.description ?? ack.message ?? null;
      const isAuthError =
        errorCode === 401 || (typeof description === 'string' && /unauthorized/i.test(description));
      return { ok, isAuthError, errorCode, description, raw: ack };
    }

    // Unknown ack shape — treat as success to avoid false negatives.
    return { ok: true, raw: ack };
  }

  _handleMessageAck(event, data, onSuccess) {
    const isJoinEvent = this._isJoinEvent(event, data);

    if (isJoinEvent) {
      // Only update currentGroup for non-chat, non-global groups.
      // Chat rooms are managed separately via leaveChat() and must never
      // overwrite currentGroup — otherwise leaveGroup() from pages like
      // /applications would inadvertently leave the active chat room.
      const isChatJoin = data.type === 'joinChat' || event === 'joinChat';
      if (data.type !== 'all' && !isChatJoin) this.currentGroup = data;
      // Persist ONLY join events to restore after reconnection
      this._markJoiningGroupEntry(data.groupId, {
        data,
        event: data?.type || event,
        joining: false,
        disconnecting: false,
      });
      // Notify UI when joins are confirmed so input can be enabled immediately
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('socketJoined', {
              detail: {
                event: event || data?.type,
                groupId: data?.groupId,
                threadId: data?.threadId,
              },
            }),
          );
        }
      } catch (e) {}

      this._flushPendingJoinCallbacks(this._callbackKey(event, data), data, null);
    }

    if (onSuccess) onSuccess(data);
  }

  _handleMessageError(event, data) {
    if (this._isJoinEvent(event, data) || this._isLeaveEvent(event, data)) {
      const key = data?.groupId || data?.threadId;
      this._releaseRoom(key, this._roomKind(event, data));
      if (this._isJoinEvent(event, data)) {
        this._flushPendingJoinCallbacks(this._callbackKey(event, data), null, {
          message: 'join_failed',
        });
      }
    }
  }

  _leaveCurrentGroup(data, onSuccess, onError) {
    if (!this.socket) return;

    const eventName = data?.type === 'leaveChat' ? 'leaveChat' : 'leaveGroup';

    this.socket.emit(eventName, data, (ok) => {
      if (ok) {
        // Do not call off(data.type) here. That removes every listener on the
        // channel, including the header badge handler, which must stay for the
        // whole session. Callers remove their own callback.
        this._handleLeaveGroupAck(data, onSuccess);
      } else {
        this._handleMessageError(eventName, data);
        if (onError) onError(data);
      }
    });
  }

  _handleLeaveGroupAck(data, onSuccess) {
    const kind = data?.type === 'leaveChat' ? 'joinChat' : data?.type;
    const leftCurrent =
      this.currentGroup &&
      kind !== 'joinChat' &&
      this._normalizeJoinKey(this.currentGroup.groupId) === this._normalizeJoinKey(data?.groupId);
    if (leftCurrent) this.currentGroup = null;
    this._releaseRoom(data.groupId, kind);
    if (onSuccess) onSuccess(data);
  }

  _processQueue() {
    for (const [key, { event, data, onSuccess, onError }] of this.messageQueue) {
      // Chat messages are retried by SupportChat's outbox, not from here.
      if (event === 'chatMessage') {
        this.messageQueue.delete(key);
        continue;
      }
      this._emitMessage(event, data, onSuccess, onError);
      this.messageQueue.delete(key);
    }
  }

  /**
   * Detaches every externally registered listener from the current socket.
   * `preserveRegistry` keeps the registry itself so `_rebindEventListeners`
   * can re-attach them to the next socket instance.
   */
  _offAll(preserveRegistry = false) {
    if (this.socket) {
      this.eventListeners.forEach((callbacks, event) => {
        if (callbacks && callbacks.forEach) {
          callbacks.forEach((cb) => this.socket.off(event, cb));
        }
      });
    }
    if (!preserveRegistry) {
      this.eventListeners.clear();
    }
  }

  _leaveAll() {
    if (!this.socket) return;
    this.joiningGroups.forEach((entry) => {
      const rooms =
        entry?.rooms && Object.keys(entry.rooms).length > 0
          ? Object.values(entry.rooms)
          : entry?.data
          ? [entry]
          : [];
      rooms.forEach((room) => {
        if (room?.data) this._leaveCurrentGroup(room.data, null, null);
      });
    });
    this.joiningGroups.clear();
  }

  getSocket() {
    return this.socket;
  }

  // Method to verify if socket is properly initialized
  isInitialized() {
    return this.socket && this.initialized;
  }

  /**
   * Public API to emit a message event (e.g. 'chatMessage') with success/error callbacks.
   * Use this instead of calling _emitMessage directly from consumer code.
   */
  sendMessage(event, data, onSuccess, onError, options) {
    this._emitMessage(event, data, onSuccess, onError, options);
  }

  /**
   * Public API to release a stuck join state for a given thread/group.
   * Call this from consumer code when a join timeout or error occurs
   * so that subsequent joins to the same thread are not blocked.
   *
   * Pass `onlyIfPending` when the release is a recovery measure rather than a
   * deliberate rejoin: a confirmed entry must survive so `_restoreJoins` can
   * recover the room on the next reconnection.
   *
   * @returns {boolean} whether the entry was actually released.
   */
  releaseJoinState(threadId, { onlyIfPending = false, kind } = {}) {
    if (!threadId) return false;

    const normalizedKey = this._normalizeJoinKey(threadId);
    const entry = normalizedKey ? this.joiningGroups.get(normalizedKey) : null;

    if (kind) {
      const room = entry?.rooms?.[kind];
      if (room) {
        if (onlyIfPending && !room.joining && !room.disconnecting) return false;
        this._releaseRoom(normalizedKey, kind);
        this._flushPendingJoinCallbacks(this._normalizeJoinKey(`${kind}:${normalizedKey}`), null, {
          message: 'join_released',
        });
        return true;
      }
      if (entry?.rooms) return false;
      if (entry && this._roomKind(entry.event, entry.data) !== kind) return false;
    }

    if (onlyIfPending) {
      if (entry && !entry.joining && !entry.disconnecting) return false;
    }

    this._releaseJoiningGroupEntry(threadId);
    this._flushPendingJoinCallbacks(threadId, null, { message: 'join_released' });
    return true;
  }

  /**
   * @param {Object}  [options]
   * @param {boolean} [options.preserveListeners] Keep externally registered
   *   listeners in the registry so a following `init()` re-attaches them.
   *   Use it whenever the socket is being recycled rather than torn down for
   *   good (context switch, token cycle) — otherwise consumers registered via
   *   `on()` are silently lost until they remount.
   */
  disconnect({ preserveListeners = false } = {}) {
    if (!this.socket) return;

    this._offAll(preserveListeners);
    if (typeof this.socket.offAny === 'function') {
      this.socket.offAny();
    }
    // A recycled socket must keep every room so the next connect sends
    // joinGroup process-instance again. A real teardown leaves them.
    if (!preserveListeners) this._leaveAll();
    this.messageQueue.clear();
    this.pendingJoinCallbacks.clear();
    // Runs the internal 'disconnect' handler (restore marks, consumer event)
    // before the instance is dropped for good.
    this.socket.disconnect();
    if (typeof this.socket.off === 'function') this.socket.off();
    this.isConnected = false;
    if (!preserveListeners) {
      this.currentGroup = null;
      this._connectionCount = 0;
      this.chatRoomOwners.clear();
      this._handshakeToken = null;
    }
    this._recycleRequested = false;
    this.socket = null;
    this.initialized = false;
    this.serverUrl = null;
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    this._stopHeartbeat();
    if (this._delayedReconnectTimer) {
      clearTimeout(this._delayedReconnectTimer);
      this._delayedReconnectTimer = null;
    }
    if (this.joiningGroupsCleanupInterval) {
      clearInterval(this.joiningGroupsCleanupInterval);
      this.joiningGroupsCleanupInterval = null;
    }
  }

  // Method to reconnect with new token
  reconnectWithNewToken(serverUrl, newToken = null) {
    // Recycle the connection without dropping consumer listeners.
    this.disconnect({ preserveListeners: true });
    this.init(serverUrl, newToken);
  }
}

export default SocketService;
